import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Get } from "../config/api/get";
import { Post } from "../config/api/post";
import { useSelector } from "react-redux";
import { CONTENT_TYPE, INVENTORY } from "../config/constants";
import { useGetAllCategories as useGetCategoriesFromModule } from "./categoryQuery";

const INVENTORY_KEY = ["inventory"];

const invalidateInventory = (queryClient, id) => {
  queryClient.invalidateQueries({ queryKey: INVENTORY_KEY });
  if (id) queryClient.invalidateQueries({ queryKey: ["inventory-item", id] });
};

/** Flat list of categories for dropdowns */
export const useGetCategories = (params = { page: 1, limit: 100 }) => {
  const query = useGetCategoriesFromModule(params);
  return {
    ...query,
    data: query.data?.docs || [],
  };
};

export const useGetAllInventory = (params = {}) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: [...INVENTORY_KEY, params],
    queryFn: async ({ signal }) => {
      const { data } = await Get(INVENTORY.getAllInventorys, token, params, { signal });
      return data || { docs: [], totalPages: 1, totalDocs: 0 };
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};

export const useGetInventoryById = (id) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["inventory-item", id],
    queryFn: async () => {
      const { data } = await Get(INVENTORY.getInventoryById + id, token);
      return data?.inventory;
    },
    enabled: !!token && !!id,
  });
};

export const useAddInventory = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ data }) => {
      const response = await Post(INVENTORY.addInventory, data, token, null, CONTENT_TYPE.FORM_DATA);
      if (response?.data?.status) return response.data;
      throw new Error(response?.data?.message || "Failed to create inventory item");
    },
    onSuccess: () => invalidateInventory(queryClient),
  });
};

export const useUpdateInventory = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await Post(INVENTORY.updateInventory + id, data, token, null, CONTENT_TYPE.FORM_DATA);
      if (response?.data?.status !== false) return response.data;
      throw new Error(response?.data?.message || "Failed to update inventory item");
    },
    onSuccess: (_data, variables) => invalidateInventory(queryClient, variables?.id),
  });
};

export const useToggleInventoryStatus = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (id) => {
      const response = await Get(INVENTORY.toggleStatus + id, token);
      if (response?.status !== false) return response;
      throw new Error(response?.message || "Failed to update status");
    },
    onSuccess: (_data, id) => invalidateInventory(queryClient, id),
  });
};

export const useDeleteInventory = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (id) => {
      const response = await Get(INVENTORY.deleteInventory + id, token);
      if (response?.status !== false) return response;
      throw new Error(response?.message || "Failed to delete inventory item");
    },
    onSuccess: () => invalidateInventory(queryClient),
  });
};

export const useIssueStock = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const response = await Post(INVENTORY.issueStock + id, payload, token);
      if (response?.data?.status) return response.data;
      throw new Error(response?.data?.message || "Failed to issue stock");
    },
    onSuccess: (_data, variables) => invalidateInventory(queryClient, variables?.id),
  });
};

export const useRemoveIssuance = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ id, issuanceId }) => {
      const response = await Post(`${INVENTORY.removeIssuance}${id}/${issuanceId}`, {}, token);
      if (response?.data?.status) return response.data;
      throw new Error(response?.data?.message || "Failed to return stock");
    },
    onSuccess: (_data, variables) => invalidateInventory(queryClient, variables?.id),
  });
};
